'use client'

import Link from 'next/link'
import { C, Callout, H, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import { MODULES } from '../_lib/curriculum'
import { useProgress } from '../_lib/progress-client'

function Recap() {
  const state = useProgress()
  return (
    <div className="my-6 grid gap-2 sm:grid-cols-2">
      {MODULES.map(m => {
        const done = m.lessons.filter(l => state.done[l.slug]).length
        return (
          <Link key={m.id} href={`/#module-${m.id}`} className="gd-card flex items-center gap-4 p-4 no-underline">
            <span className="gd-serif text-[34px] leading-none" style={{ color: done === m.lessons.length ? 'var(--gd-green)' : 'var(--gd-rule-2)' }}>
              {String(m.n).padStart(2, '0')}
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">{m.title}</span>
              <span className="gd-label !text-[10px]">
                {done}/{m.lessons.length} lessons
              </span>
            </span>
          </Link>
        )
      })}
    </div>
  )
}

const READING: [string, string][] = [
  ['Learning representations by back-propagating errors (Rumelhart, Hinton & Williams, 1986)', 'Backpropagation, which you derived by hand in Module 7.'],
  ['ImageNet Classification with Deep Convolutional Neural Networks (Krizhevsky et al., 2012)', 'AlexNet: the convolutional network that started the deep-learning boom.'],
  ['Adam: A Method for Stochastic Optimization (Kingma & Ba, 2014)', 'The optimiser you’ve used for most of this course.'],
  ['Deep Residual Learning for Image Recognition (He et al., 2015)', 'Residual connections, from “Making deep networks train”.'],
  ['A Neural Probabilistic Language Model (Bengio et al., 2003)', 'The model you built in “A neural language model”.'],
  ['Efficient Estimation of Word Representations in Vector Space (Mikolov et al., 2013)', 'word2vec, from the lesson of the same name.'],
  ['Attention Is All You Need (Vaswani et al., 2017)', 'The paper that introduced the transformer. You can now read it.'],
  ['BERT: Pre-training of Deep Bidirectional Transformers (Devlin et al., 2018)', 'The encoder from “Encoders, decoders & BERT”.'],
  ['Scaling Laws for Neural Language Models (Kaplan et al., 2020)', 'The original power-law plots.'],
  ['Training Compute-Optimal Large Language Models (Hoffmann et al., 2022)', 'The “Chinchilla” paper from the scaling lesson.'],
  ['Training language models to follow instructions with human feedback (Ouyang et al., 2022)', 'How RLHF turned GPT-3 into an assistant.'],
  ['LoRA: Low-Rank Adaptation of Large Language Models (Hu et al., 2021)', 'Cheap fine-tuning, from “Fine-tuning & LoRA”.'],
  ['Retrieval-Augmented Generation for Knowledge-Intensive NLP Tasks (Lewis et al., 2020)', 'The RAG recipe.'],
  ['Denoising Diffusion Probabilistic Models (Ho et al., 2020)', 'The diffusion recipe from “Diffusion models”.'],
  ['Learning Transferable Visual Models From Natural Language Supervision (Radford et al., 2021)', 'CLIP, from “Seeing and reading together”.'],
]

export default function WhatsNext() {
  return (
    <Lesson>
      <Step>
        <H>Look how far you’ve come</H>
        <P>
          You started with the number line. Along the way you learned to program, then met algebra, logarithms, trigonometry,
          vectors and matrices, eigenvectors, derivatives and integrals, probability, likelihood and entropy. Then you built —
          yourself, in code — linear and logistic regression, k-nearest neighbours, decision trees, k-means, neural networks, an
          autograd engine, a convolutional network, word2vec, recurrent networks, attention and a working GPT. Finally you looked
          at the frontier: assistants, fine-tuning, retrieval, reinforcement learning, diffusion, multimodal models, efficiency,
          reasoning, evaluation and safety. Along the way you finished nine projects of your own.
        </P>
        <Recap />
        <P>
          The key ideas are the ones you’ve met here. The current frontier is mostly these same ideas, scaled up and refined.
        </P>
      </Step>

      <Step>
        <H>Your capstone</H>
        <P>
          You’ve finished nine projects. For a capstone, open <Link href="/lab" className="gd-link">the Lab</Link> (it has no time
          limit) and combine them into something new. Some ideas:
        </P>
        <UL>
          <li>
            <strong>Ask your own notes.</strong> Paste your own notes in as documents, retrieve the best chunk with TF-IDF, and
            have your GPT (trained on the same notes) continue from it.
          </li>
          <li>
            <strong>Push the digit reader.</strong> Combine the CNN, data augmentation and early stopping. How close to 100% can
            you get on held-out digits?
          </li>
          <li>
            <strong>Run a proper evaluation.</strong> Compare two of your models (bigram vs. RNN, MLP vs. CNN) with bootstrap error
            bars. Is the winner really better?
          </li>
          <li>
            <strong>Improve the sampler.</strong> Implement top-p (nucleus) sampling for your GPT, and compare it with top-k at
            different temperatures.
          </li>
        </UL>
        <Callout kind="try">
          Everything runs on your device, so nothing you type leaves your browser. Go wild.
        </Callout>
      </Step>

      <Step>
        <H>Where to go next</H>
        <P>Some classic papers that will now make sense to you, in roughly the order you met their ideas:</P>
        <div className="gd-card my-6 overflow-hidden">
          {READING.map(([title, why], i) => (
            <div key={title} className="border-b px-5 py-3 last:border-b-0" style={{ borderColor: 'var(--gd-rule)' }}>
              <p className="m-0 font-semibold">
                <span className="gd-num mr-2" style={{ color: 'var(--gd-ink-3)' }}>
                  {i + 1}.
                </span>
                {title}
              </p>
              <p className="m-0 mt-0.5 text-[14.5px]" style={{ color: 'var(--gd-ink-2)' }}>
                {why}
              </p>
            </div>
          ))}
        </div>
        <P>
          And practically: learn a production framework (PyTorch or JAX — the concepts map one-to-one onto the <C>Tensor</C> and{' '}
          <C>Value</C> classes you know), train on real datasets, and read the code of open models. You’ll recognise every piece.
        </P>
      </Step>

      <Step gate="q-final">
        <H>One last question</H>
        <Quiz
          id="q-final"
          question="Underneath everything in this course — from the spam filter to the GPT — what is the single loop that makes a model learn?"
          options={[
            { label: 'Predict, measure the loss, compute gradients with the chain rule, step downhill — repeat.', correct: true },
            { label: 'Write better rules by hand.', why: 'That’s what machine learning replaced, back in the very first lesson.' },
            { label: 'Memorise the training data.', why: 'Memorising is overfitting. Learning is about generalising.' },
          ]}
          explain="That’s it. Everything else is choosing the model, the data and the loss — and scale. Thank you for learning with Gradient."
        />
      </Step>
    </Lesson>
  )
}
